import type { ReactNode } from 'react';
import { CheckIcon, usePlanChoice, type PlanDto } from '@huaca/ui';
// The landing's price splitter, reused rather than rewritten: it is a pure function over the
// SERVER'S label and the only place that knows how to take "₪2 לחודש" apart without parsing a
// number out of it. A second copy here is a second thing to fix when a label shape changes.
import { splitPrice } from './landing-facts';

/** An LTR-isolated numeral island — the design system's own, so digits sit correctly in Hebrew. */
function Num({ children }: { children: ReactNode }) {
  return <span className="huaca-num">{children}</span>;
}

/**
 * Features every plan has. These are the table's ROWS.
 *
 * The intersection, not the union, and that is the whole design: tenders' tiers grant the same
 * routes and the same single seat, so nine cells of identical ticks is the screen arguing with
 * itself. Rows that every column ticks state the shared floor once; anything a single plan has to
 * itself is not a comparison and rides with that plan's price instead (see `extrasOf`).
 *
 * Ordered by the first plan's list so the sequence is the server's, not a Set's iteration order.
 */
function sharedFeatures(plans: PlanDto[]): string[] {
  const [first, ...rest] = plans;
  if (!first) return [];
  return first.display.features.filter((f) => rest.every((p) => p.display.features.includes(f)));
}

/** What this plan has that the others do not — rendered beside its price, never as a mostly-empty row. */
function extrasOf(plan: PlanDto, shared: string[]): string[] {
  return plan.display.features.filter((f) => !shared.includes(f));
}

/**
 * The single coral action.
 *
 * The design system allows ONE `.btn-cta` per screen, and `badge` alone cannot pick it: two plans
 * carry one ("להתחלה" on the trial, "הכי פופולרי" on pro) and honouring both is exactly the
 * two-coral-buttons problem this screen was redrawn to fix. Badges are free-form server copy, so
 * nothing here can read intent out of the words. `purchasable` can: the coral marks the recommended
 * PURCHASE, which a no-card trial is not. Falls back to nothing rather than guessing.
 */
function featuredIdOf(plans: PlanDto[]): string | null {
  return plans.find((p) => p.purchasable && p.display.badge)?.id ?? null;
}

/**
 * Plan selection for tenders, as a ledger.
 *
 * WHY NOT `PlanSelector`. The platform's card list implies three things to compare; these tiers
 * differ by ONE number — the daily search cap in `display.meterValue` — and everything else about
 * them is identical. The table says so directly: a tick in every column for the shared floor, then
 * the one row that actually separates the tiers, set large and tinted.
 *
 * The FLOW is not duplicated. `usePlanChoice` is the same hook `PlanSelector` uses, so the fetch,
 * the owner gate, the trial-vs-checkout branch and the error mapping are one implementation across
 * both screens.
 */
export function PlanLedger({ productId }: { productId: string }) {
  const { plans, error, busyPlan, mayBuy, choose } = usePlanChoice(productId);

  if (error && !plans) return <p role="alert">{error}</p>;
  if (!plans) return <p data-testid="plans-loading">טוען תוכניות…</p>;

  const shared = sharedFeatures(plans);
  const featuredId = featuredIdOf(plans);
  // One label for a whole row of numbers; the backend's own contract test pins every tier to the
  // same wording, so the first plan that carries one speaks for the row.
  const meterLabel = plans.find((p) => p.display.meterLabel)?.display.meterLabel ?? null;
  const hasMeter = meterLabel !== null && plans.some((p) => typeof p.display.meterValue === 'number');

  return (
    <div className="huaca-plans tenders-plans" data-testid="plan-selector">
      <h1>בחירת תוכנית</h1>
      <p className="tenders-plans__lede">
        {hasMeter
          ? 'שלוש התוכניות נותנות אותו דבר. ההבדל היחיד הוא בשורה המודגשת.'
          : 'כל התוכניות נותנות את אותן היכולות.'}
      </p>

      {!mayBuy && (
        <p role="note" data-testid="not-owner-note">
          רק בעלים של הארגון יכולים להתחיל תוכנית. בקשו מהם לבחור תוכנית לצוות.
        </p>
      )}

      {/* Four columns do not fit a phone; without this the PAGE scrolls sideways instead of the
          table. Deliberately NOT `.tenders-tablewrap` — that carries the search table's surface,
          border and radius, and this design is a ruled sheet rather than a panel. */}
      <div className="tenders-ledgerwrap">
        <table className="tenders-ledger">
          <thead>
            <tr>
              {/* The corner. Empty on purpose — it labels neither a row nor a column. */}
              <td className="tenders-ledger__corner" />
              {plans.map((plan) => (
                <th key={plan.id} scope="col" data-testid={`plan-${plan.id}`}>
                  <span className="tenders-ledger__name">{plan.display.name}</span>
                  {plan.display.badge && (
                    <span className="tenders-ledger__badge" data-testid="badge">
                      {plan.display.badge}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {shared.map((feature) => (
              <tr key={feature}>
                <th scope="row" className="tenders-ledger__lab">
                  {feature}
                </th>
                {plans.map((plan) => (
                  // Every column ticks, by construction — `shared` IS the intersection. The tick is
                  // decorative here and the row header carries the meaning, so it is aria-hidden
                  // rather than announced four times over.
                  <td key={plan.id} className="tenders-ledger__tick">
                    <CheckIcon className="huaca-icon--sm" />
                  </td>
                ))}
              </tr>
            ))}

            {hasMeter && (
              <tr className="tenders-ledger__row--meter">
                <th scope="row" className="tenders-ledger__lab tenders-ledger__lab--meter">
                  {meterLabel}
                </th>
                {plans.map((plan) => (
                  <td key={plan.id} className="tenders-ledger__meter" data-testid="meter">
                    {typeof plan.display.meterValue === 'number' ? (
                      // Grouped HERE, not in config: the server sends a number precisely so three
                      // tiers cannot arrive with three different thousands separators.
                      <Num>{plan.display.meterValue.toLocaleString('he-IL')}</Num>
                    ) : (
                      '—'
                    )}
                  </td>
                ))}
              </tr>
            )}

            <tr>
              <th scope="row" className="tenders-ledger__lab">
                מחיר
              </th>
              {plans.map((plan) => {
                const { amount, unit } = splitPrice(plan.display.priceLabel);
                const extras = extrasOf(plan, shared);
                return (
                  <td key={plan.id} className="tenders-ledger__price" data-testid="price">
                    {/* Only the NUMERAL is direction-isolated. Isolating the whole label flips the
                        Hebrew, rendering "₪3 לחודש" as "לחודש 3₪" — see Landing.tsx's Price. */}
                    <Num>{amount}</Num>
                    {unit ? ` ${unit}` : ''}
                    {/* Said ONCE, here, and emphasised. "ללא כרטיס אשראי" is the strongest reason
                        to pick the trial, and it belongs in the trial's own column rather than in a
                        closing line under the table where it sits away from the thing it describes. */}
                    {extras.length > 0 && (
                      <span className="tenders-ledger__extra">{extras.join(' · ')}</span>
                    )}
                  </td>
                );
              })}
            </tr>
          </tbody>

          {mayBuy && (
            <tfoot>
              <tr>
                <td className="tenders-ledger__corner" />
                {plans.map((plan) => (
                  <td key={plan.id} className="tenders-ledger__act">
                    <button
                      type="button"
                      className={plan.id === featuredId ? 'btn-cta' : 'tenders-ledger__pick'}
                      disabled={busyPlan !== null}
                      onClick={() => void choose(plan)}
                      data-testid={`choose-${plan.id}`}
                    >
                      {busyPlan === plan.id
                        ? 'מעבד…'
                        : plan.trialDays !== null
                          ? 'התחלת ניסיון'
                          : 'בחירת תוכנית'}
                    </button>
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {error && (
        <p role="alert" data-testid="plan-error">
          {error}
        </p>
      )}
    </div>
  );
}
